import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import client, { getClose, getRole, recountCash, fixDepartments, fixExtras, fixCloseDate, pushCloseToSheet, ocrReceipt } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { formatDate, money } from "@/lib/format";
import { CARDLINE, DENOMS, DEPARTMENTS, TILL_LABEL, ZLINE, SHORTAGE_REASONS } from "@/lib/constants";
import { Share2, Copy, Trash2, Flag, Wand2, Camera, Pencil, X as XIcon, MessageSquare, Upload } from "lucide-react";
import CashCounter from "@/components/CashCounter";
import { CoachButton } from "@/components/CoachDialog";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function CloseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [tapeImages, setTapeImages] = useState([]);
  const [extraNote, setExtraNote] = useState("");
  const [fixOpen, setFixOpen] = useState(false);
  const [fixDenoms, setFixDenoms] = useState({});
  const [fixNote, setFixNote] = useState("");
  const [fixSaving, setFixSaving] = useState(false);
  const [fixDeptOpen, setFixDeptOpen] = useState(false);
  const [fixDeptTotals, setFixDeptTotals] = useState({});
  const [fixDeptNote, setFixDeptNote] = useState("");
  const [fixDeptSaving, setFixDeptSaving] = useState(false);
  const [fixDateOpen, setFixDateOpen] = useState(false);
  const [fixDateVal, setFixDateVal] = useState("");
  const [fixDateNote, setFixDateNote] = useState("");
  const [fixDateSaving, setFixDateSaving] = useState(false);
  const [fixExtrasOpen, setFixExtrasOpen] = useState(false);
  const [fixExtrasVals, setFixExtrasVals] = useState({ cheques: 0, gift_cards: 0, other: 0 });
  const [fixExtrasNote, setFixExtrasNote] = useState("");
  const [fixExtrasSaving, setFixExtrasSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const tapeInputRef = React.useRef(null);
  const role = getRole();

  useEffect(() => { getClose(id).then(setC).catch(() => toast.error("Couldn't load")); }, [id]);

  const toggleFlag = async () => {
    if (!c) return;
    const next = !c.needs_review;
    setC({ ...c, needs_review: next });
    try { await client.patch(`/closes/${id}/flag`, { needs_review: next }); }
    catch { toast.error("Couldn't update flag"); setC({ ...c, needs_review: !next }); }
  };

  const addTapePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const encode = (file) => new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const img = new Image();
        img.onload = () => {
          const max = 1600;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          res(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.onerror = rej;
        img.src = reader.result;
      };
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });
    const encoded = await Promise.all(files.map(encode));
    setTapeImages((prev) => [...prev, ...encoded]);
    if (tapeInputRef.current) tapeInputRef.current.value = "";
  };

  const removeTape = (idx) => setTapeImages((prev) => prev.filter((_, i) => i !== idx));

  const runAudit = async () => {
    if (!tapeImages.length) { toast.error("Add at least one tape photo first"); return; }
    setAuditing(true);
    try {
      const { data } = await client.post(`/closes/${id}/audit`, {
        images_base64: tapeImages,
        extra_note: extraNote,
      });
      setC({ ...c, audit_summary: data.audit_summary, audit_at: data.audit_at, needs_review: true });
      setTapeImages([]); setExtraNote("");
      toast.success("Tape audited");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed — try again");
    } finally {
      setAuditing(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await client.delete(`/closes/${id}`);
      toast.success("Close deleted");
      nav("/dashboard", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 403 ? "Owner PIN required to delete" : "Couldn't delete");
      setDeleting(false);
    }
  };

  const handlePushToSheet = async () => {
    setPushing(true);
    try {
      await pushCloseToSheet(id);
      toast.success("Pushed to Google Sheet");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) toast.error("Set the sheet webhook URL on the dashboard first");
      else toast.error(e?.response?.data?.detail || "Couldn't push to the sheet");
    } finally {
      setPushing(false);
    }
  };

  if (!c) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header title="Close" onBack={() => nav(-1)} />
        <main className="max-w-md mx-auto px-5 py-10 text-center text-[#5C7566]">Loading…</main>
      </div>
    );
  }

  const text = [
    `Till Trax — ${TILL_LABEL[c.till] || c.till}`,
    formatDate(c.created_at),
    "",
    `Moneris total: ${money(c.moneris_total)}`,
    `Tips: ${money(c.tips)}`,
    `Card sales: ${money(c.card_sales)} (register ${CARDLINE[c.till]}: ${money(c.register_card_line)})`,
    `Cash counted: ${money(c.cash_counted)}  Float: ${money(c.float_amount)}  Deposit: ${money(c.cash_deposit)}`,
    `Cheques: ${money(c.cheques)}  Gift cards: ${money(c.gift_cards)}  Other: ${money(c.other)}`,
    "",
    `TOTAL [1]: ${money(c.total_one)}`,
    `Drawer total (${ZLINE[c.till]}): ${money(c.register_drawer_total)}`,
    `Difference: ${money(c.difference)}`,
    c.notes ? `\nNote: ${c.notes}` : "",
  ].join("\n");

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Till Trax", text });
        return;
      }
    } catch { /* fall through */ }
    const ok = await copyText(text);
    if (ok) toast.success("Copied");
  };
  const copy = async () => { const ok = await copyText(text); if (ok) toast.success("Copied"); };

  const diff = Number(c.difference || 0);
  const balanced = Math.abs(diff) < 0.005;
  const big = Math.abs(diff) > 10;
  const toneCfg = balanced
    ? { color: "text-[#1B7B4B]", bg: "bg-[#1B7B4B]/10", label: "Balanced" }
    : big
      ? { color: "text-[#B91C1C]", bg: "bg-[#B91C1C]/10", label: diff > 0 ? "OVER" : "SHORT" }
      : { color: "text-[#92520A]", bg: "bg-[#D97706]/10", label: diff > 0 ? "OVER" : "SHORT" };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Close detail" onBack={() => nav(-1)} />
      <main className="max-w-md mx-auto px-5 py-5 space-y-4" data-testid="close-detail">
        <div>
          <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase">{String(c.mode).replace(/_/g, " ")}</div>
          <h2 className="text-2xl text-[#0D311E] mt-1" style={{ fontFamily: "'Fraunces', serif" }}>{TILL_LABEL[c.till] || c.till}</h2>
          {!fixDateOpen ? (
            <p className="text-[#5C7566] text-sm flex items-center gap-2">
              <span data-testid="close-date-label">{formatDate(c.created_at)}</span>
              <button
                data-testid="fix-date-open"
                onClick={() => {
                  setFixDateVal(c.closed_on || (c.created_at || "").slice(0, 10));
                  setFixDateNote("");
                  setFixDateOpen(true);
                }}
                aria-label="Change date"
                className="h-7 w-7 rounded-full bg-white border border-[#E8E4DA] text-[#2B533E] flex items-center justify-center"
              >
                <Pencil size={12} />
              </button>
            </p>
          ) : (
            <div className="mt-2 bg-white rounded-2xl border border-[#E8E4DA] p-3 space-y-2" data-testid="fix-date-panel">
              <label className="text-xs text-[#5C7566] block">Move this close to which date?</label>
              <input
                data-testid="fix-date-input"
                type="date"
                value={fixDateVal}
                onChange={(e) => setFixDateVal(e.target.value)}
                className="w-full h-11 rounded-xl border border-[#E8E4DA] px-3 bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A]"
              />
              <input
                data-testid="fix-date-note"
                value={fixDateNote}
                onChange={(e) => setFixDateNote(e.target.value)}
                placeholder="Why the fix? e.g. uploaded on the wrong day (optional)"
                className="w-full h-11 rounded-xl border border-[#E8E4DA] px-3 bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A]"
                autoComplete="off"
              />
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  data-testid="fix-date-cancel"
                  disabled={fixDateSaving}
                  onClick={() => setFixDateOpen(false)}
                  className="h-11 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="fix-date-save"
                  disabled={fixDateSaving || !fixDateVal}
                  onClick={async () => {
                    setFixDateSaving(true);
                    try {
                      const r = await fixCloseDate(id, fixDateVal, fixDateNote);
                      setC({ ...c, closed_on: r.closed_on, created_at: r.created_at, date_fixes: [...(c.date_fixes || []), r.date_fix] });
                      setFixDateOpen(false);
                      toast.success("Date updated");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Couldn't update date");
                    } finally {
                      setFixDateSaving(false);
                    }
                  }}
                  className="h-11 rounded-xl bg-[#1A5D3A] text-white text-sm disabled:opacity-50"
                >
                  {fixDateSaving ? "Saving…" : "Save new date"}
                </button>
              </div>
              {(c.date_fixes || []).length > 0 && (
                <div className="mt-2 text-[11px] text-[#5C7566] space-y-0.5" data-testid="date-fix-history">
                  {c.date_fixes.map((r, i) => (
                    <div key={i}>Moved {r.old_closed_on} → {r.new_closed_on} ({formatDate(r.at)} by {r.by}){r.note ? ` · ${r.note}` : ""}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`rounded-2xl ${toneCfg.bg} p-5 text-center border border-[#E8E4DA]`}>
          <div className="text-[10px] tracking-[0.22em] text-[#5C7566] uppercase mb-1">{toneCfg.label}</div>
          <div className={`text-5xl tabular-nums ${toneCfg.color}`} style={{ fontFamily: "'Fraunces', serif" }}>
            {money(Math.abs(diff))}
          </div>
        </div>

        <Section title="Card">
          <Row label="Moneris total" value={money(c.moneris_total)} />
          <Row label="Tips (paid in cash from tip float)" value={money(c.tips)} />
          <Row label="Card sales" value={money(c.card_sales)} bold />
          <Row label={`Register ${CARDLINE[c.till]}`} value={money(c.register_card_line)} hint={c.card_match ? "matches" : "off"} />
        </Section>

        <Section title="Cash">
          <Row
            label={c.float_in_count === false ? "Cash counted (deposit only — float set aside)" : "Cash counted (full till, float in)"}
            value={money(c.cash_counted)}
          />
          <Row label={c.float_in_count === false ? "Float (set aside, not in count)" : "Float"} value={money(c.float_amount)} />
          <Row label="Cash deposit" value={money(c.cash_deposit)} bold />
          {(c.recounts || []).length > 0 && (
            <div className="mt-3 text-xs text-[#5C7566] bg-[#F5F2EA] p-3 rounded-xl space-y-1" data-testid="recount-history">
              {c.recounts.map((r, i) => (
                <div key={i}>
                  Count fixed {formatDate(r.at)} by {r.by} — cash {money(r.old_cash_counted)} → {money(r.new_cash_counted)}
                  {r.note ? ` · ${r.note}` : ""}
                </div>
              ))}
            </div>
          )}
          {!fixOpen ? (
            <button
              data-testid="fix-cash-open"
              onClick={() => { setFixDenoms({ ...(c.denominations || {}) }); setFixNote(""); setFixOpen(true); }}
              className="w-full h-11 mt-3 rounded-xl bg-white border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2"
            >
              <Pencil size={14} /> Fix the cash count
            </button>
          ) : (
            <FixCashPanel
              c={c}
              denoms={fixDenoms}
              setDenoms={setFixDenoms}
              note={fixNote}
              setNote={setFixNote}
              saving={fixSaving}
              onCancel={() => setFixOpen(false)}
              onSave={async () => {
                setFixSaving(true);
                try {
                  const r = await recountCash(id, fixDenoms, fixNote);
                  setC({
                    ...c,
                    denominations: { ...fixDenoms },
                    cash_counted: r.cash_counted,
                    cash_deposit: r.cash_deposit,
                    total_one: r.total_one,
                    difference: r.difference,
                    recounts: [...(c.recounts || []), r.recount],
                  });
                  setFixOpen(false);
                  toast.success("Cash count fixed — totals updated");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't save the new count");
                } finally {
                  setFixSaving(false);
                }
              }}
            />
          )}
        </Section>

        <Section title="Departments">
          <DeptList close={c} />
          {c.groups_photo_skip_reason && (
            <div className="mt-3 text-xs text-[#92520A] bg-[#D97706]/10 border border-[#D97706]/30 p-3 rounded-xl" data-testid="dept-photo-skip-reason-note">
              Tape photo skipped — reason given: {c.groups_photo_skip_reason}
            </div>
          )}
          {(c.dept_fixes || []).length > 0 && (
            <div className="mt-3 text-xs text-[#5C7566] bg-[#F5F2EA] p-3 rounded-xl space-y-1" data-testid="dept-fix-history">
              {c.dept_fixes.map((r, i) => (
                <div key={i}>Depts fixed {formatDate(r.at)} by {r.by}{r.note ? ` · ${r.note}` : ""}</div>
              ))}
            </div>
          )}
          {!fixDeptOpen ? (
            <button
              data-testid="fix-dept-open"
              onClick={() => {
                const known = {};
                (DEPARTMENTS[c.till] || []).forEach((d) => { known[d.key] = Number((c.group_totals || {})[d.key] || 0); });
                // carry over user-added custom_* entries and their labels only — never the other till's keys
                Object.entries(c.group_totals || {}).forEach(([k, v]) => {
                  if (k.startsWith("custom_") || k.startsWith("_label_")) known[k] = v;
                });
                setFixDeptTotals(known);
                setFixDeptNote("");
                setFixDeptOpen(true);
              }}
              className="w-full h-11 mt-3 rounded-xl bg-white border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2"
            >
              <Pencil size={14} /> Fix the departments
            </button>
          ) : (
            <FixDeptPanel
              c={c}
              totals={fixDeptTotals}
              setTotals={setFixDeptTotals}
              note={fixDeptNote}
              setNote={setFixDeptNote}
              saving={fixDeptSaving}
              onCancel={() => setFixDeptOpen(false)}
              onSave={async () => {
                setFixDeptSaving(true);
                try {
                  // split labels from numbers
                  const totals = {};
                  const labels = {};
                  Object.entries(fixDeptTotals).forEach(([k, v]) => {
                    if (k.startsWith("_label_")) labels[k.slice(7)] = String(v);
                    else totals[k] = Number(v || 0);
                  });
                  const r = await fixDepartments(id, totals, labels, fixDeptNote);
                  setC({
                    ...c,
                    group_totals: r.group_totals,
                    pro_shop_transfer: r.pro_shop_transfer,
                    restaurant_revenue: r.restaurant_revenue,
                    dept_fixes: [...(c.dept_fixes || []), r.dept_fix],
                  });
                  setFixDeptOpen(false);
                  toast.success("Departments updated");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't save the fix");
                } finally {
                  setFixDeptSaving(false);
                }
              }}
            />
          )}
        </Section>

        <Section title="Other">
          <Row label="Cheques" value={money(c.cheques)} />
          <Row label="Gift cards" value={money(c.gift_cards)} />
          <Row label="Other" value={money(c.other)} />
          {(c.extras_fixes || []).length > 0 && (
            <div className="mt-3 text-xs text-[#5C7566] bg-[#F5F2EA] p-3 rounded-xl space-y-1" data-testid="extras-fix-history">
              {c.extras_fixes.map((r, i) => (
                <div key={i}>
                  Fixed {formatDate(r.at)} by {r.by} — gift cards {money(r.old_gift_cards)} → {money(r.new_gift_cards)}
                  {r.note ? ` · ${r.note}` : ""}
                </div>
              ))}
            </div>
          )}
          {!fixExtrasOpen ? (
            <button
              data-testid="fix-extras-open"
              onClick={() => {
                setFixExtrasVals({ cheques: Number(c.cheques || 0), gift_cards: Number(c.gift_cards || 0), other: Number(c.other || 0) });
                setFixExtrasNote("");
                setFixExtrasOpen(true);
              }}
              className="w-full h-11 mt-3 rounded-xl bg-white border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2"
            >
              <Pencil size={14} /> Fix cheques / gift cards / other
            </button>
          ) : (
            <FixExtrasPanel
              c={c}
              vals={fixExtrasVals}
              setVals={setFixExtrasVals}
              note={fixExtrasNote}
              setNote={setFixExtrasNote}
              saving={fixExtrasSaving}
              onCancel={() => setFixExtrasOpen(false)}
              onSave={async () => {
                setFixExtrasSaving(true);
                try {
                  const r = await fixExtras(id, Number(fixExtrasVals.cheques || 0), Number(fixExtrasVals.gift_cards || 0), Number(fixExtrasVals.other || 0), fixExtrasNote);
                  setC({
                    ...c,
                    cheques: r.cheques,
                    gift_cards: r.gift_cards,
                    other: r.other,
                    total_one: r.total_one,
                    difference: r.difference,
                    extras_fixes: [...(c.extras_fixes || []), r.extras_fix],
                  });
                  setFixExtrasOpen(false);
                  toast.success("Updated — totals recalculated");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't save the fix");
                } finally {
                  setFixExtrasSaving(false);
                }
              }}
            />
          )}
        </Section>

        <Section title="Final">
          <Row label="TOTAL [1]" value={money(c.total_one)} bold />
          <Row label={`Drawer total (${ZLINE[c.till]})`} value={money(c.register_drawer_total)} />
          <Row label="Difference" value={money(c.difference)} bold />
          {c.shortage_reason && (
            <div className="mt-3 text-sm text-[#2B533E] bg-[#F5F2EA] p-3 rounded-xl">
              <strong>Reason:</strong> {(SHORTAGE_REASONS.find((r) => r.id === c.shortage_reason) || {}).label || c.shortage_reason}
              {c.shortage_reason_other && <div className="mt-1 text-[#5C7566]">{c.shortage_reason_other}</div>}
            </div>
          )}
          {c.notes && <div className="mt-3 text-sm text-[#2B533E] bg-[#F5F2EA] p-3 rounded-xl">Note: {c.notes}</div>}
        </Section>

        {c.audit_summary && (
          <Section title="Tape audit">
            <div className="prose prose-sm max-w-none text-[#2B533E] whitespace-pre-wrap" data-testid="audit-summary">
              {c.audit_summary}
            </div>
            {c.audit_at && <div className="text-xs text-[#5C7566] mt-2">Audited {formatDate(c.audit_at)}</div>}
          </Section>
        )}

        {role === "owner" && (
          <Section title="Audit the full tape">
            <p className="text-sm text-[#2B533E]">
              Photograph the full register tape — multiple shots if it's long. AI will list refunds, voids and other clues that could explain the variance.
            </p>
            <input
              ref={tapeInputRef}
              data-testid="audit-tape-input"
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={addTapePhotos}
            />
            <button
              data-testid="audit-add-photo"
              onClick={() => tapeInputRef.current?.click()}
              className="w-full h-12 rounded-xl bg-white border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2 mt-3"
            >
              <Camera size={16} /> Add tape photo {tapeImages.length > 0 ? `(${tapeImages.length} added)` : ""}
            </button>
            {tapeImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3" data-testid="audit-thumbs">
                {tapeImages.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt={`Tape ${i+1}`} className="w-full h-24 object-cover rounded-lg border border-[#E8E4DA] bg-[#F5F2EA]" />
                    <button
                      data-testid={`audit-remove-${i}`}
                      onClick={() => removeTape(i)}
                      aria-label="Remove"
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[#0D311E] text-white flex items-center justify-center"
                    >
                      <XIcon size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              data-testid="audit-extra-note"
              rows={2}
              value={extraNote}
              onChange={(e) => setExtraNote(e.target.value)}
              placeholder="Extra context for the audit (optional)"
              className="w-full mt-3 rounded-xl border border-[#E8E4DA] p-3 bg-[#FDFBF7] text-[#0D311E] outline-none focus:border-[#1A5D3A] text-sm"
              autoComplete="off"
            />
            <button
              data-testid="audit-run"
              onClick={runAudit}
              disabled={auditing || !tapeImages.length}
              className="w-full mt-3 h-12 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Wand2 size={16} /> {auditing ? "Reading the tape…" : "Run audit"}
            </button>
          </Section>
        )}

        {(c.moneris_photo_b64 || c.register_photo_b64 || (c.extra_photos_b64 || []).length > 0 || c.photo_check_summary) && (
          <Section title="Photos">
            {c.photo_check_summary && (
              <div data-testid="photo-check-summary" className="text-sm text-[#2B533E] bg-[#F5F2EA] p-3 rounded-xl whitespace-pre-wrap mb-3">
                <strong>Photo check{c.photo_check_at ? ` — ${formatDate(c.photo_check_at)}` : ""}:</strong>
                {"\n"}{c.photo_check_summary}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3" data-testid="close-photos">
              {c.moneris_photo_b64 && <img alt="Moneris" src={c.moneris_photo_b64} className="rounded-xl border border-[#E8E4DA]" />}
              {c.register_photo_b64 && <img alt="Register" src={c.register_photo_b64} className="rounded-xl border border-[#E8E4DA]" />}
              {(c.extra_photos_b64 || []).map((p, i) => (
                <img key={i} alt={`Extra ${i + 1}`} src={p} className="rounded-xl border border-[#E8E4DA]" />
              ))}
            </div>
          </Section>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2">
          <button data-testid="detail-share" onClick={share} className="h-12 rounded-xl bg-[#1A5D3A] text-white flex items-center justify-center gap-2 active:scale-95">
            <Share2 size={16} /> Share
          </button>
          <button data-testid="detail-copy" onClick={copy} className="h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] flex items-center justify-center gap-2 active:scale-95">
            <Copy size={16} /> Copy
          </button>
        </div>

        <button
          data-testid="detail-flag"
          onClick={toggleFlag}
          className={`w-full h-11 rounded-xl flex items-center justify-center gap-2 text-sm border ${c.needs_review ? "bg-[#B91C1C]/10 text-[#7F1D1D] border-[#B91C1C]/30" : "bg-white text-[#2B533E] border-[#E8E4DA]"}`}
        >
          <Flag size={14} fill={c.needs_review ? "currentColor" : "none"} /> {c.needs_review ? "Flagged for review" : "Flag this close for review"}
        </button>

        {Math.abs(Number(c.difference || 0)) >= 0 && <CoachButton closeId={c.id} label="Talk it through with the assistant" />}

        {role === "owner" && (
          <button
            data-testid="detail-push-sheet"
            onClick={handlePushToSheet}
            disabled={pushing}
            className="w-full h-11 rounded-xl bg-white border border-[#1A5D3A]/30 text-[#1A5D3A] hover:bg-[#1A5D3A]/5 flex items-center justify-center gap-2 text-sm disabled:opacity-50 mt-1"
          >
            <Upload size={14} /> {pushing ? "Pushing…" : "Push this close to the sheet again"}
          </button>
        )}

        {role === "owner" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid="detail-delete"
                className="w-full h-11 rounded-xl border border-[#B91C1C]/30 text-[#B91C1C] hover:bg-[#B91C1C]/5 flex items-center justify-center gap-2 text-sm mt-1"
              >
                <Trash2 size={14} /> Delete this close
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#FDFBF7] border-[#E8E4DA]">
              <AlertDialogHeader>
                <AlertDialogTitle style={{ fontFamily: "'Fraunces', serif" }} className="text-[#0D311E]">
                  Delete this close?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[#2B533E]">
                  This removes the close from the shared dashboard, including its photos and tip log entry. It can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="detail-delete-cancel" className="border-[#E8E4DA] bg-white text-[#2B533E]">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="detail-delete-confirm"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="bg-[#B91C1C] hover:bg-[#991414] text-white"
                >
                  {deleting ? "Deleting…" : "Yes, delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </main>
    </div>
  );
}

function DeptList({ close }) {
  const depts = DEPARTMENTS[close.till] || [];
  const totals = close.group_totals || {};
  // Only show this till's depts + user-added custom_* entries — never bleed keys from the OTHER till.
  const customs = Object.entries(totals).filter(([k]) => String(k).startsWith("custom_"));
  const grandTotal = depts.reduce((s, d) => s + (Number(totals[d.key]) || 0), 0)
    + customs.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  if (!depts.length && !customs.length) {
    return <div className="text-sm text-[#5C7566]">No departments recorded.</div>;
  }
  return (
    <div className="space-y-2" data-testid="dept-list">
      {depts.map((d) => {
        const v = Number(totals[d.key] || 0);
        return (
          <Row
            key={d.key}
            label={`${d.label}${d.code ? "  ·  " + d.code : d.group ? "  ·  G" + d.group : ""}`}
            value={money(v)}
          />
        );
      })}
      {customs.map(([k, v]) => (
        <Row
          key={k}
          label={totals[`_label_${k}`] || String(k).replace(/^custom_/, "").replace(/_/g, " ")}
          value={money(Number(v || 0))}
        />
      ))}
      <Row label="Department total" value={money(grandTotal)} bold />
    </div>
  );
}

function FixDeptPanel({ c, totals, setTotals, note, setNote, saving, onCancel, onSave }) {
  const depts = DEPARTMENTS[c.till] || [];
  // Only show user-added custom_* entries — never bleed keys from the OTHER till in.
  const customEntries = Object.entries(totals).filter(([k]) => k.startsWith("custom_"));
  const grand = depts.reduce((s, d) => s + (Number(totals[d.key]) || 0), 0)
    + customEntries.reduce((s, [, v]) => s + (Number(v) || 0), 0);
  const [ocrBusy, setOcrBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  const setVal = (k, v) => setTotals({ ...totals, [k]: Number(v || 0) });

  const addCustom = () => {
    const label = window.prompt("Department name (e.g. 'D09 Lessons')");
    if (!label) return;
    const key = "custom_" + label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    setTotals({ ...totals, [key]: 0, [`_label_${key}`]: label });
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const max = 1600;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = w; canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            res(canvas.toDataURL("image/jpeg", 0.82));
          };
          img.onerror = rej;
          img.src = reader.result;
        };
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const data = await ocrReceipt("register_groups", b64, c.till);
      const merged = { ...totals };
      Object.entries(data || {}).forEach(([k, v]) => { if (v !== null && v !== undefined) merged[k] = Number(v); });
      setTotals(merged);
      toast.success("Read the tape — review the numbers");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read the tape");
    } finally {
      setOcrBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 space-y-3" data-testid="fix-dept-panel">
      <p className="text-sm text-[#2B533E]">
        Type in the missing department amount, or snap the Z-tape and the AI will read it.
      </p>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="fix-dept-photo-input" />
      <button
        data-testid="fix-dept-photo"
        onClick={() => fileRef.current?.click()}
        disabled={ocrBusy}
        className="w-full h-11 rounded-xl bg-white border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Camera size={14} /> {ocrBusy ? "Reading…" : "Snap the Z-tape (optional)"}
      </button>
      <div className="space-y-2">
        {depts.map((d) => (
          <DeptInput
            key={d.key}
            label={`${d.label}${d.code ? "  ·  " + d.code : d.group ? "  ·  G" + d.group : ""}`}
            testId={`fix-dept-${d.key}`}
            value={totals[d.key] || 0}
            onChange={(v) => setVal(d.key, v)}
          />
        ))}
        {customEntries.map(([k, v]) => (
          <DeptInput
            key={k}
            label={totals[`_label_${k}`] || k.replace(/^custom_/, "").replace(/_/g, " ")}
            testId={`fix-dept-${k}`}
            value={v || 0}
            onChange={(nv) => setVal(k, nv)}
          />
        ))}
      </div>
      <button
        data-testid="fix-dept-add-custom"
        onClick={addCustom}
        className="w-full h-11 rounded-xl border border-dashed border-[#1A5D3A]/40 text-[#2B533E] text-sm flex items-center justify-center gap-2"
      >
        + Add unknown dept
      </button>
      <div className="rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] p-3 flex items-center justify-between" data-testid="fix-dept-total">
        <span className="text-sm text-[#5C7566]">Department total</span>
        <span className="tabular-nums text-xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>{money(grand)}</span>
      </div>
      <input
        data-testid="fix-dept-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why the fix? e.g. D08 was skipped (optional)"
        className="w-full h-11 rounded-xl border border-[#E8E4DA] px-3 bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A]"
        autoComplete="off"
      />
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="fix-dept-cancel" onClick={onCancel} disabled={saving} className="h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm disabled:opacity-50">
          Cancel
        </button>
        <button data-testid="fix-dept-save" onClick={onSave} disabled={saving} className="h-12 rounded-xl bg-[#1A5D3A] text-white text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save departments"}
        </button>
      </div>
    </div>
  );
}

function DeptInput({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-white rounded-xl border border-[#E8E4DA] px-3 h-12">
      <label className="text-sm text-[#2B533E] flex-1 truncate">{label}</label>
      <div className="flex items-center gap-1 text-[#0D311E]">
        <span className="text-sm text-[#5C7566]">$</span>
        <input
          data-testid={testId}
          inputMode="decimal"
          type="number"
          step="0.01"
          value={value === 0 ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          placeholder="0.00"
          className="w-24 text-right tabular-nums bg-transparent outline-none"
        />
      </div>
    </div>
  );
}

function FixCashPanel({ c, denoms, setDenoms, note, setNote, saving, onCancel, onSave }) {
  const isCloseMode = c.mode === "end_of_day" || c.mode === "shift_change";
  const floatIn = c.float_in_count !== false;
  const newCash = Math.round(DENOMS.reduce((s, d) => s + (Number(denoms[d.key]) || 0) * d.value, 0) * 100) / 100;
  const newDeposit = isCloseMode ? Math.round((newCash - (floatIn ? Number(c.float_amount || 0) : 0)) * 100) / 100 : 0;
  const newTotal = Math.round((Number(c.card_sales || 0) + newDeposit + Number(c.cheques || 0) + Number(c.gift_cards || 0) + Number(c.other || 0)) * 100) / 100;
  const newDiff = Math.round((newTotal - Number(c.register_drawer_total || 0)) * 100) / 100;
  const balanced = Math.abs(newDiff) < 0.005;
  return (
    <div className="mt-3 space-y-3" data-testid="fix-cash-panel">
      <p className="text-sm text-[#2B533E]">
        {floatIn
          ? <>Recount the WHOLE drawer — <strong>including the {money(c.float_amount)} float</strong>. The totals update as you go.</>
          : <>Recount <strong>just the deposit pile</strong> — the {money(c.float_amount)} float was set aside and isn't part of this count.</>}
      </p>
      <CashCounter value={denoms} onChange={setDenoms} />
      <div className={`rounded-xl p-3 text-sm flex items-center justify-between ${balanced ? "bg-[#1B7B4B]/10 text-[#155F39]" : "bg-[#D97706]/10 text-[#92520A]"}`} data-testid="fix-cash-preview">
        <span>New difference</span>
        <span className="tabular-nums font-medium">
          {balanced ? "Balanced ✓" : `${newDiff > 0 ? "Over" : "Short"} ${money(Math.abs(newDiff))}`}
        </span>
      </div>
      <input
        data-testid="fix-cash-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why the fix? e.g. float wasn't counted in (optional)"
        className="w-full h-11 rounded-xl border border-[#E8E4DA] px-3 bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A]"
        autoComplete="off"
      />
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="fix-cash-cancel" onClick={onCancel} disabled={saving} className="h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm disabled:opacity-50">
          Cancel
        </button>
        <button data-testid="fix-cash-save" onClick={onSave} disabled={saving} className="h-12 rounded-xl bg-[#1A5D3A] text-white text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save new count"}
        </button>
      </div>
    </div>
  );
}

function FixExtrasPanel({ c, vals, setVals, note, setNote, saving, onCancel, onSave }) {
  const newTotal = Math.round((Number(c.card_sales || 0) + Number(c.cash_deposit || 0) + Number(vals.cheques || 0) + Number(vals.gift_cards || 0) + Number(vals.other || 0)) * 100) / 100;
  const newDiff = Math.round((newTotal - Number(c.register_drawer_total || 0)) * 100) / 100;
  const balanced = Math.abs(newDiff) < 0.005;
  return (
    <div className="mt-3 space-y-3" data-testid="fix-extras-panel">
      <p className="text-sm text-[#2B533E]">
        Correct the amounts below — TOTAL[1] and the difference update as you go.
      </p>
      <div className="space-y-2">
        <DeptInput label="Cheques" testId="fix-extras-cheques" value={vals.cheques || 0} onChange={(v) => setVals({ ...vals, cheques: v })} />
        <DeptInput label="AGC Gift cards / AG Bucks" testId="fix-extras-gift" value={vals.gift_cards || 0} onChange={(v) => setVals({ ...vals, gift_cards: v })} />
        <DeptInput label="Other" testId="fix-extras-other" value={vals.other || 0} onChange={(v) => setVals({ ...vals, other: v })} />
      </div>
      <div className={`rounded-xl p-3 text-sm flex items-center justify-between ${balanced ? "bg-[#1B7B4B]/10 text-[#155F39]" : "bg-[#D97706]/10 text-[#92520A]"}`} data-testid="fix-extras-preview">
        <span>New difference</span>
        <span className="tabular-nums font-medium">
          {balanced ? "Balanced ✓" : `${newDiff > 0 ? "Over" : "Short"} ${money(Math.abs(newDiff))}`}
        </span>
      </div>
      <input
        data-testid="fix-extras-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Why the fix? e.g. wrong gift card amount (optional)"
        className="w-full h-11 rounded-xl border border-[#E8E4DA] px-3 bg-[#FDFBF7] text-[#0D311E] text-sm outline-none focus:border-[#1A5D3A]"
        autoComplete="off"
      />
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="fix-extras-cancel" onClick={onCancel} disabled={saving} className="h-12 rounded-xl bg-[#F5F2EA] border border-[#E8E4DA] text-[#2B533E] text-sm disabled:opacity-50">
          Cancel
        </button>
        <button data-testid="fix-extras-save" onClick={onSave} disabled={saving} className="h-12 rounded-xl bg-[#1A5D3A] text-white text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save the fix"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E4DA] p-5">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C7566] mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value, bold, hint }) {
  return (
    <div className={`flex justify-between ${bold ? "text-[#0D311E] text-lg" : "text-[#2B533E]"}`}>
      <span className="flex items-baseline gap-2">
        {label}
        {hint && <span className="text-xs text-[#5C7566]">· {hint}</span>}
      </span>
      <span className="tabular-nums" style={{ fontFamily: bold ? "'Fraunces', serif" : undefined }}>{value}</span>
    </div>
  );
}
